import React, { useEffect, useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { ChevronDown, ChevronUp, Minus, Plus } from "lucide-react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { ExpandableProps } from "src/interfaces/functionalComponentInterface";
import User from "../img/humanIcon.svg";
import DollarSign from "../img/dollarSign.svg";
import inputFieldDollar from "../img/inputFieldDollar.svg";

const Expandable: React.FC<ExpandableProps> = ({
  title,
  onChange,
  weekIndex,
  data,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [tempInputValue, setTempInputValue] = useState<string>(
    data.accountValue.toString()
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  useEffect(() => {
    setTempInputValue(data.accountValue.toString());
  }, [data.accountValue]);

  const handleBlur = () => {
    const numericValue = parseFloat(tempInputValue.replace(/[^0-9.-]+/g, ""));

    if (!tempInputValue || isNaN(numericValue)) {
      setTempInputValue(
        new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(0)
      );
      onChange(weekIndex, "accountValue", 0);
    } else {
      setTempInputValue(
        new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numericValue)
      );
      onChange(weekIndex, "accountValue", numericValue);
    }
  };

  const handleInputChange = (value: string, field: string) => {
    if (field === "accountValue") {
      setTempInputValue(value);

      const numericValue = parseFloat(value.replace(/[^0-9.-]+/g, ""));
      if (!isNaN(numericValue)) {
        setError(null);
        onChange(weekIndex, field, numericValue);
      }
    }

    if (/^\d*$/.test(value)) {
      const numericValue = Number(value);

      if (field === "numberOfClients") {
        setError(null);
        onChange(weekIndex, field, numericValue);
      }
    }
  };

  const handleFocus = () => {
    const numericValue = parseFloat(tempInputValue.replace(/[^0-9.-]+/g, ""));
    setTempInputValue(
      !isNaN(numericValue) && numericValue !== 0 ? numericValue.toString() : ""
    );
  };

  const handleDecrement = () => {
    const newValue = Math.max(0, data.numberOfClients - 1);
    onChange(weekIndex, "numberOfClients", newValue);
  };

  const handleIncrement = () => {
    const newValue = data.numberOfClients + 1;
    onChange(weekIndex, "numberOfClients", newValue);
  };

  return (
    <Collapsible
      className="border rounded-lg p-4 mb-4 bg-white custom-collapse"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <CollapsibleTrigger className="w-full flex justify-between items-center cursor-pointer">
        <h2 className="text-sm md:text-base font-semibold">{title}</h2>

        <div className="flex flex-row items-center gap-5 md:gap-10">
          {!isOpen && (
            <>
              <div className="flex flex-row gap-2 items-center justify-center">
                <img src={User} alt="user icon" />
                <span className="text-sm md:text-base font-semibold text-gray-600">
                  {data.numberOfClients}
                </span>
              </div>
              <div className="flex flex-row gap-2 items-center justify-center">
                <img src={DollarSign} alt="dollar sign icon" />
                <span className="text-sm md:text-base font-semibold text-gray-600">
                  {data.accountValue.toLocaleString()}
                </span>
              </div>
            </>
          )}
        </div>
        <div
          className={`rounded-full ${
            isOpen ? "bg-buttonOpen" : "bg-buttonClose"
          }`}
        >
          {isOpen ? <ChevronUp className="text-white" /> : <ChevronDown />}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4">
        <div className="flex flex-col gap-4 md:flex-row">
          {/* Number of Clients */}
          <div className="flex-1">
            <Label className="block text-sm md:text-base font-medium mb-1 text-expandableText">
              Number of Households
            </Label>

            <div className="relative flex items-center">
              <button
                onClick={handleDecrement}
                className={`absolute left-2 bg-primary hover:bg-primary-foreground rounded-full w-6 h-6 text-white flex items-center justify-center ${
                  data.numberOfClients <= 0
                    ? "opacity-50 pointer-events-none"
                    : ""
                }`}
                disabled={data.numberOfClients <= 0}
              >
                <Minus size={18} />
              </button>
              <Input
                type="text"
                value={data.numberOfClients}
                onChange={(e) => {
                  handleInputChange(e.target.value, "numberOfClients");
                }}
                className="text-center w-full pl-10 pr-10 text-sm md:text-base font-medium bg-white"
              />
              <button
                onClick={handleIncrement}
                className={`absolute right-2 bg-primary hover:bg-primary-foreground rounded-full w-6 h-6 text-white flex items-center justify-center `}
              >
                <Plus size={18} />
              </button>
            </div>
            {error && (
              <p className="text-red-500 text-sm font-medium mt-1">{error}</p>
            )}
          </div>

          {/* Account Value */}
          <div className="flex-1">
            <Label className="block text-sm md:text-base font-medium mb-1 text-expandableText">
              Account Value
            </Label>
            <div className="relative flex items-center">
              <img src={inputFieldDollar} alt="" className="absolute left-2" />
              <Input
                type="text"
                value={tempInputValue}
                onChange={(e) => {
                  if (parseFloat(e.target.value) < 0) {
                    e.target.value = "0";
                  }
                  handleInputChange(e.target.value, "accountValue");
                }}
                onBlur={handleBlur}
                onFocus={handleFocus}
                className="bg-white w-full border rounded-md p-2 text-center text-sm md:text-base font-medium"
              />
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default Expandable;
